import { useEffect, useState } from 'react';
import { Spinner } from '@/components/Spinner';
import { Pager } from '@/components/Pager';
import { SearchBox } from '@/components/form/SearchBox';
import { CustomSelect } from '@/components/form/CustomSelect';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useAsyncData } from '@/hooks/useAsyncData';
import { showToast } from '@/lib/toast';
import { fetchBanners, createBanner, updateBanner, type BannerRow } from '@/api/banners';
import { BannerForm } from './BannerForm';
import { BannerTile } from './BannerTile';

/** Режим вкладки: список, создание или правка конкретного баннера. */
export type BannerFormMode = { mode: 'create' } | { mode: 'edit'; banner: BannerRow } | null;

/** Фильтр по статусу показа; пустое значение — все записи. */
const STATUS_OPTIONS = [
  { value: '', label: 'Все статусы' },
  { value: '1', label: 'Включённые' },
  { value: '0', label: 'Отключённые' },
];

/**
 * Вкладка "Баннеры" раздела «Маркетинг» — 1:1 старые Orchid-экраны
 * Marketing*Screen (см. докблок BannerController в Laravel). Режим формы
 * приходит сверху: кнопки «Добавить»/«Удалить» стоят в шапке страницы.
 */
export function BannersTab({
  form,
  onOpenForm,
  onCloseForm,
  reloadToken,
  onReload,
}: {
  form: BannerFormMode;
  onOpenForm: (form: BannerFormMode) => void;
  onCloseForm: () => void;
  reloadToken: number;
  onReload: () => void;
}) {
  const [search, searchInput, setSearchInput] = useDebouncedSearch();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  /** Раскрыт ровно один баннер: раскрытие другого схлопывает предыдущий. */
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const formOpen = form !== null;

  useEffect(() => {
    setPage(1);
  }, [search, status]);

  // Открытая форма занимает всё место списка — грузить его в это время незачем.
  const {
    data: list,
    error,
    setData,
  } = useAsyncData(
    formOpen
      ? null
      : () => fetchBanners({ search: search || undefined, active: status || undefined, page }),
    [search, status, page, reloadToken, formOpen],
    'Не удалось загрузить список баннеров.',
  );
  const rows = list?.items ?? null;
  const meta = list?.meta ?? null;

  if (form?.mode === 'create') {
    return (
      <BannerForm
        onCancel={onCloseForm}
        onSave={async (payload) => {
          const created = await createBanner(payload);
          onCloseForm();
          showToast(`Баннер «${created.name}» создан`);
          onReload();
        }}
      />
    );
  }

  if (form?.mode === 'edit') {
    const editing = form.banner;
    return (
      <BannerForm
        initial={editing}
        onCancel={onCloseForm}
        onSave={async (payload) => {
          const updated = await updateBanner(editing.id, payload);
          onCloseForm();
          showToast(`Баннер «${updated.name}» обновлён`);
          onReload();
        }}
      />
    );
  }

  return (
    <>
      <div className="filter-row">
        <SearchBox value={searchInput} onChange={setSearchInput} placeholder="Поиск по названию" />
        <div className="group-select">
          <CustomSelect value={status} options={STATUS_OPTIONS} onChange={setStatus} />
        </div>
      </div>

      {error && <p className="field-error">{error}</p>}

      {rows === null ? (
        <div className="panel-loading">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <p className="cell-muted">Баннеры не найдены.</p>
      ) : (
        <div className="catalog-tiles">
          {rows.map((row) => (
            <BannerTile
              key={row.id}
              banner={row}
              expanded={expandedId === row.id}
              onToggleExpand={() => setExpandedId((id) => (id === row.id ? null : row.id))}
              onEditRequest={() => onOpenForm({ mode: 'edit', banner: row })}
              onDeleted={onReload}
              onPatched={(updated) =>
                setData((prev) =>
                  prev ? { ...prev, items: prev.items.map((r) => (r.id === updated.id ? updated : r)) } : prev,
                )
              }
            />
          ))}
        </div>
      )}

      {meta && meta.lastPage > 1 && (
        <div className="table-footer">
          <span className="table-footer__status">
            Страница {meta.currentPage} из {meta.lastPage} · всего {meta.total}
          </span>
          <Pager page={meta.currentPage} lastPage={meta.lastPage} onPageChange={setPage} />
        </div>
      )}
    </>
  );
}
